import * as React from 'react';
import { observer } from 'mobx-react';
import { Panel, ListGroup, ListGroupItem, Button } from 'react-bootstrap';

import { observable, action, computed } from 'mobx';
import { inject } from '../../utils/decorators';
import { ApiStore } from '../../stores/ApiStore';
import { UiStore } from '../../stores/UiStore';
import { IVorgangObjekteResponse, IObjekt, IAuftrag, AGT_4GAS, AGT_KOMBI, AGT_OPAZIMETER, IVorgang } from '../../models/ApiTypes';
import FaIcon from '../site/FaIcon';

interface IObjektListeProps {
  objekte?: IObjekt[];
  editable?: boolean;
  showTitle?: boolean;
  auftrag?: IAuftrag;
  vorgang?: IVorgang;
  onAuswahlAufheben?: () => void;
}

@observer
export default class ObjektListe extends React.Component<IObjektListeProps, {}> {

  static defaultProps: Partial<IObjektListeProps> = {
    editable: true,
    showTitle: true
  };

  @inject(ApiStore) apiStore: ApiStore;
  @inject(UiStore) uiStore: UiStore;

  @observable objekte: IObjekt[];

  objektKuerzel = new Map([
    ['ses', 'Scheinwerfer-Einstellprüfgerät'],
    ['agt', 'Abgasmessgerät'],
    ['bps', 'Bremsprüfstand'],
    ['dmg', 'Druckmessgerät']
  ]);

  componentDidMount() {
    this.refresh();
  }

  async refresh() {
    const {  objekte } = this.props;
    if (objekte) {
      this.setObjekte(objekte);
    }
  }

  /** Liefert den zum Auftrag bzw. Vorgang passenden Objektbezeichner (SEP für SES, AGT für Kal. Abgasmessgerät, etc.) */
  @computed get objektBezeichner() {
    const { auftrag, vorgang } = this.props;
    let kategorie: string = auftrag && auftrag.kategorie ? auftrag.kategorie : '';
    if (vorgang) {
      kategorie = vorgang.objekttyp;
    }
    return this.objektKuerzel.get(kategorie.toLocaleLowerCase()) || 'Objekt';
  }

  @action setObjekte(objekte: IVorgangObjekteResponse) {
    this.objekte = objekte;
  }

  render() {
    const { editable, onAuswahlAufheben, auftrag, vorgang } = this.props;
    let kategorie: string = auftrag && auftrag.kategorie ? auftrag.kategorie : '';
    if (vorgang) {
      kategorie = vorgang.objekttyp;
    }
    let url = kategorie !== '' ? `objekte/anlegen/${kategorie}` : '';
    return (
      <Panel
        header={this.props.showTitle ? this.objektBezeichner : undefined}
        footer={editable && (
          <div className="text-right">
            {(url !== '') && <Button onClick={() => this.uiStore.navigateTo(url)} bsStyle="success">Neu</Button>}
            {((!this.objekte) || (this.objekte.length > 0)) && <Button onClick={(onAuswahlAufheben) ? () => onAuswahlAufheben() : undefined} bsStyle="danger">Entfernen</Button>}
            <Button onClick={() => this.uiStore.navigateTo('objekte')}>Auswählen</Button>
          </div>
        )}
      >
        <ListGroup fill={true}>
          {!this.objekte ? (
            <ListGroupItem>lade Daten...</ListGroupItem>
          ) : (this.objekte.length === 0) ? (
            <ListGroupItem>Es wurde noch kein Objekt gewählt</ListGroupItem>
          ) : this.objekte.map((objekt, index) => (
            <ObjektListItem
              key={index}
              objekt={objekt}
              zusatzinfo={true}
            />
          ))}
        </ListGroup>
      </Panel>
    );
  }
}

interface IObjektListItemZusatzInfo {
  objekt: IObjekt;
}

/**
 * Zeigt zusätzliche Informationen aus den Meta-Daten eines Objektes anhand seiner Kategorie an
 * Z.B. Genauigkeitsklasse beim AGT, Übersetzungsverhältnis beim BPS
 */
class ObjektListItemZusatzInfo extends React.Component<IObjektListItemZusatzInfo, {}> {

  findMeta(bezeichnung: string) {
    const { objekt } = this.props;
    if (!objekt.meta) { return undefined; }
    let metaDatum = objekt.meta.find(meta => meta.bezeichnung === bezeichnung);
    if (metaDatum) {
      return metaDatum.inhalt;
    }
    return undefined;
  }

  private renderAGT4GasInfo() {
    let genauigkeitsKlasse = this.findMeta('agt_genauigkeitsklasse');
    return (
      <span className="text-muted">
        <b>Genauigkeitsklasse: </b> {genauigkeitsKlasse}<br/>
      </span>
    );
  }

  private renderAGTOpazimeterInfo() {
    let fgKoeffizient = this.findMeta('agt_fehlergrenze');
    let messkammerLaenge = this.findMeta('agt_messkammerlänge');
    return (
      <span className="text-muted">
        <b>Fehlergrenze: </b> ± {fgKoeffizient} m<sup>-1</sup><br/>
        <b>Messkammerlänge: </b>{messkammerLaenge} mm<br/>
      </span>
    );
  }

  private renderAGTInfo() {
    const art = this.props.objekt.art ? this.props.objekt.art.toLocaleLowerCase() : '';
    return (
      <div>
        {((art === AGT_4GAS) || (art === AGT_KOMBI)) && this.renderAGT4GasInfo()}
        {((art === AGT_OPAZIMETER) || (art === AGT_KOMBI)) && this.renderAGTOpazimeterInfo()}
      </div>
    );
  }

  private renderBPSInfo() {
    let hebelArmLaenge = this.findMeta('hebelarmlänge');
    let nenndurchmesserRolle = this.findMeta('rollendurchmesser');
    let uebersetzungZaehler = this.findMeta('übersetzungsverhältnis_zähler');
    let uebersetzungNenner = this.findMeta('übersetzungsverhältnis_nenner');
    let anzeigeArt = this.findMeta('anzeigeart');
    let anzeigeAufloesung = this.findMeta('anzeigeaufloesung');
    return (
      <span>
        <b>Art:</b> {this.props.objekt.name}<br/>
        {this.props.objekt.art === 'rolle' && <div>
          <b>Hebelarmlänge:</b> {hebelArmLaenge} mm<br/>
          <b>Nenndurchmesser Rolle:</b> {nenndurchmesserRolle} mm<br/>
          <b>Übersetzung:</b> {uebersetzungZaehler} : {uebersetzungNenner}<br/>
        </div>}
        <b>Anzeige:</b> <span className="capitalize">{anzeigeArt}</span> mit {anzeigeAufloesung} N Auflösung<br/>
      </span>
    );
  }

  render() {
    const { objekt } = this.props;
    let typ = objekt.typ.toLocaleLowerCase();
    // Je nach Typ des Objektes sollen zusätzliche, spezifische Informationen angezeigt werden die direkt bei der Anzeige für den Nutzer relevant sind
    if (typ === 'agt') {
      return (
        <span className="text-muted">
          {this.renderAGTInfo()}
        </span>
      );
    }
    if (typ === 'bps') {
      return (
        <span className="text-muted">
          {this.renderBPSInfo()}
        </span>
      );
    }
    return null;
  }
}

export type ObjektListItemAuswahlCallback = (objekt: IObjekt) => void;

interface IObjektListItemProps {
  objekt: IObjekt;
  onObjektAuswahl?: ObjektListItemAuswahlCallback;
  zusatzinfo: boolean;
}

export class ObjektListItem extends React.Component<IObjektListItemProps, {}> {

  render() {
    const { objekt, onObjektAuswahl, zusatzinfo } = this.props;
    return (
      <ListGroupItem
        onClick={(onObjektAuswahl) ? () => onObjektAuswahl(objekt) : undefined}
        header={<span>{objekt.objektid}</span>}
      >
        <span className="text-muted">
          <b>Typ:</b> {objekt.hersteller} {objekt.typbezeichnung}<br/>
          <b>S/N:</b> {objekt.sernr}<br/>
          {objekt.bemerkung && objekt.bemerkung !== '-' && <div><FaIcon icon="file-text-o" /> {objekt.bemerkung}</div>}
        </span>
        {zusatzinfo && <ObjektListItemZusatzInfo objekt={objekt}/>}
      </ListGroupItem>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/objekte/ObjektListe.tsx